// Telemetria SIP centralizzata su Sentry. Unico punto del codebase che conosce Sentry.
// PII-safe by design: l'API non ha campi per numeri di telefono o credenziali.
// `enabled` rispecchia il gate di Sentry.init in main.ts (`!!runConfig.SENTRY_ENABLED`):
// così telemetria-attiva ⇔ Sentry-inizializzato. Testabile via createSipTelemetry().
import * as Sentry from "@sentry/vue";
import { runConfig } from "@/config/runtime-config";

export type TelemetryLevel = "info" | "warning" | "error";

export interface TelemetryEventContext {
  level: TelemetryLevel;
  tags: Record<string, string>;
  extra?: Record<string, number | string | undefined>;
}

export interface TelemetrySink {
  captureMessage: (message: string, context: TelemetryEventContext) => void;
  flush: (timeoutMs: number) => Promise<boolean>;
}

export type PopupCloseReason =
  | "main-alive-timeout"
  | "shutdown-message"
  | "role-changed"
  | "before-unload"
  | "duplicate-no-leadership";

export type SipTransaction = "register" | "invite" | "transport" | "timeout";
export type SipFailureType = "transport" | "auth" | "timeout";

/** Azioni del keep-alive anti-freeze del popup (GAIA-2587). */
export type SipKeepAliveAction = "started" | "stopped" | "blocked";
/** Chi stava per aprire il popup quando la navigazione è stata impedita (GAIA-2674). */
export type SipPopupOpenSource = "auto-open" | "manual" | "phone-click";
/** Trigger che ha innescato un tentativo di recovery della registrazione. */
export type SipRecoveryTrigger = "resume" | "visible";
/**
 * Perché una risposta non è andata a buon fine. `no-call` = non c'era più nulla da
 * rispondere (coda che ritira, chiamante che riaggancia); `accept-failed` =
 * `invitation.accept()` ha fallito, tipicamente sul microfono. Distinguerli è
 * l'unico modo per sapere in prod perché il tasto verde "non fa niente" (GAIA-2664).
 */
export type SipAnswerFailureReason = "no-call" | "accept-failed";
/** Da dove è arrivato il comando di risposta: la barra stessa o una tab di lavoro. */
export type SipAnswerSource = "bar" | "view" | "prediction";

export interface SipTelemetry {
  mainAliveThrottled(p: { gapMs: number; visibility: DocumentVisibilityState }): void;
  popupClosed(p: {
    reason: PopupCloseReason;
    heartbeatGapMs: number;
    visibility: DocumentVisibilityState;
  }): void;
  connectionFailed(p: {
    failureType?: SipFailureType;
    sipCode?: number;
    transaction: SipTransaction;
    retryAfter?: number;
  }): void;
  retryOutcome(p: { trigger: "auto" | "manual"; result: "registered" | "failed" }): void;
  popupCloseAverted(p: { heartbeatGapMs: number; visibility: DocumentVisibilityState }): void;
  /** Ciclo di vita del keep-alive audio che tiene il popup fuori dal freeze di Chrome. */
  keepAlive(p: { action: SipKeepAliveAction; reason?: string }): void;
  /**
   * La main app stava per navigare — cioè ricaricare — il popup vivo con
   * `window.open(url, POPUP_WINDOW_NAME)`, perché l'heartbeat lo dava per morto
   * mentre il Web Lock owner diceva il contrario. Impedito. Ogni evento qui è una
   * chiamata che PRIMA di GAIA-2674 sarebbe caduta: misura la prevalenza in prod.
   */
  popupNavigationPrevented(p: { source: SipPopupOpenSource }): void;
  /** Il popup è stato congelato dal browser (Page Lifecycle `freeze`): finestra cieca alle INVITE. */
  pageFrozen(p: { visibility: DocumentVisibilityState }): void;
  /** Il popup è tornato attivo (`resume` o visibilitychange→visible) dopo essere stato nascosto. */
  pageResumed(p: { hiddenMs: number; wasFrozen: boolean }): void;
  /**
   * La registrazione SIP è caduta in modo non intenzionale (refresh throttlato/rifiutato):
   * l'interno risulta ancora "connesso" all'operatore ma è cieco lato centralino.
   */
  registrationLost(p: { fromState: string; visibility: DocumentVisibilityState }): void;
  /**
   * L'operatore ha chiesto di rispondere e la chiamata NON è stata risposta.
   * Ogni evento qui è una chiamata persa con un click sopra: prima di GAIA-2664
   * questo caso non lasciava traccia da nessuna parte.
   */
  answerFailed(p: { reason: SipAnswerFailureReason; source: SipAnswerSource }): void;
  /** Tentativo di recovery della registrazione dopo un wake dallo stato nascosto/congelato. */
  registrationRecovery(p: {
    trigger: SipRecoveryTrigger;
    result: "reregistered" | "healthy" | "failed" | "skipped-in-call" | "deferred-to-retry";
  }): void;
  flush(timeoutMs: number): Promise<boolean>;
}

const sentrySink: TelemetrySink = {
  captureMessage: (message, context) =>
    Sentry.captureMessage(message, {
      level: context.level,
      tags: context.tags,
      extra: context.extra,
    }),
  flush: (timeoutMs) => Sentry.flush(timeoutMs),
};

export function createSipTelemetry(deps: { enabled: boolean; sink: TelemetrySink }): SipTelemetry {
  const { enabled, sink } = deps;

  function emit(message: string, context: TelemetryEventContext): void {
    if (!enabled) return;
    sink.captureMessage(message, context);
  }

  return {
    mainAliveThrottled({ gapMs, visibility }) {
      emit("sip.main_alive_throttled", {
        level: "warning",
        tags: { visibility },
        extra: { gapMs },
      });
    },
    popupClosed({ reason, heartbeatGapMs, visibility }) {
      emit("sip.popup_closed", {
        level: reason === "main-alive-timeout" ? "error" : "info",
        tags: { reason, visibility },
        extra: { heartbeatGapMs },
      });
    },
    connectionFailed({ failureType, sipCode, transaction, retryAfter }) {
      emit("sip.connection_failed", {
        level: "warning",
        tags: {
          transaction,
          failureType: failureType ?? "none",
          sipCode: sipCode !== undefined ? String(sipCode) : "none",
        },
        extra: { retryAfter },
      });
    },
    retryOutcome({ trigger, result }) {
      emit("sip.retry_outcome", {
        level: "info",
        tags: { trigger, result },
      });
    },
    popupCloseAverted({ heartbeatGapMs, visibility }) {
      emit("sip.popup_close_averted", {
        level: "info",
        tags: { visibility },
        extra: { heartbeatGapMs },
      });
    },
    keepAlive({ action, reason }) {
      emit("sip.keep_alive", {
        level: action === "blocked" ? "warning" : "info",
        tags: { action },
        extra: { reason },
      });
    },
    popupNavigationPrevented({ source }) {
      emit("sip.popup_navigation_prevented", {
        level: "warning",
        tags: { source },
      });
    },
    pageFrozen({ visibility }) {
      emit("sip.page_frozen", {
        level: "warning",
        tags: { visibility },
      });
    },
    pageResumed({ hiddenMs, wasFrozen }) {
      emit("sip.page_resumed", {
        level: "info",
        tags: { wasFrozen: String(wasFrozen) },
        extra: { hiddenMs },
      });
    },
    registrationLost({ fromState, visibility }) {
      emit("sip.registration_lost", {
        level: "error",
        tags: { fromState, visibility },
      });
    },
    answerFailed({ reason, source }) {
      emit("sip.answer_failed", {
        level: "error",
        tags: { reason, source },
      });
    },
    registrationRecovery({ trigger, result }) {
      emit("sip.registration_recovery", {
        level: result === "failed" ? "warning" : "info",
        tags: { trigger, result },
      });
    },
    flush(timeoutMs) {
      if (!enabled) return Promise.resolve(true);
      return sink.flush(timeoutMs);
    },
  };
}

export const sipTelemetry = createSipTelemetry({
  enabled: !!runConfig.SENTRY_ENABLED,
  sink: sentrySink,
});
